import React, { useState } from "react";
import RadioButton from "../../ui/widgets/radiobutton";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import { useMessageSource } from "react-message-source";
import { RadioButtonItem } from "../../ui/widgets/radiobutton/types";
import Uploader from "./uploader";
import {
  AttachmentMetaDocument,
  CardApplicationAttachmentsDocument,
  CardApplicationDocument,
} from "../../api/commercial/generated-types";
import GridItem from "../../ui/layout/page/grid-item";
import CustomTypography from "../../ui/widgets/typography";

export interface PersonDocumentUploadProps {
  cardApplication: CardApplicationDocument;
  personAttachments: CardApplicationAttachmentsDocument[];
}

export const PersonDocumentUpload = ({
  cardApplication,
  personAttachments,
}: PersonDocumentUploadProps): React.JSX.Element => {
  const { getMessage } = useMessageSource();
  const [activeAttachmentDocumentType, setActiveAttachmentDocumentType] =
    useState<
      CardApplicationAttachmentsDocument["attachmentDocumentType"] | undefined
    >(personAttachments[0].attachmentDocumentType);
  const [isUploadInProgress, setIsUploadInProgress] = useState<boolean>(false);

  const applicationRef = cardApplication.applicationRef;

  const radioButtonItems: RadioButtonItem[] = personAttachments
    .filter(
      (attachment) =>
        attachment.attachmentDocumentType !== "AUTHENTICITY_PROOF",
    )
    .map((attachment: CardApplicationAttachmentsDocument) => {
      const radioButtonItem: RadioButtonItem = {
        id: attachment.attachmentDocumentType,
        value: attachment.attachmentDocumentType,
        label: getMessage(
          `DocumentUpload.radiobutton-option-${attachment.attachmentDocumentType}`,
        ),
      };
      return radioButtonItem;
    });

  const getAttachmentMeta = ({
    attachmentDocumentType,
    getFirstAttachment,
    getSecondAttachment,
  }: {
    attachmentDocumentType: CardApplicationAttachmentsDocument["attachmentDocumentType"];
    getFirstAttachment?: boolean;
    getSecondAttachment?: boolean;
  }): AttachmentMetaDocument | undefined => {
    const attachmentDocument: CardApplicationAttachmentsDocument =
      personAttachments.find(
        (attachment: CardApplicationAttachmentsDocument) => {
          return attachment.attachmentDocumentType === attachmentDocumentType;
        },
      );
    if (getFirstAttachment) {
      return attachmentDocument.attachmentMetaDocument
        ? attachmentDocument.attachmentMetaDocument[0]
        : undefined;
    }
    if (
      getSecondAttachment &&
      attachmentDocument.attachmentMetaDocument !== null &&
      attachmentDocument.attachmentMetaDocument.length > 1
    ) {
      return attachmentDocument.attachmentMetaDocument[1];
    }
    return undefined;
  };

  const isOneMoreUploadForEdgeCase =
    activeAttachmentDocumentType === "PASSPORT_ID_OR_DRIVER_LICENCE" ||
    activeAttachmentDocumentType === "PASSPORT_OR_ID";
  const { firstName, lastName, gender } = cardApplication.cardholder;
  const cardholder = `${
    gender === "FEMALE" ? getMessage("Gender.MS") : getMessage("Gender.MR")
  } ${firstName} ${lastName}`;

  return (
    <div className="scd-section-pusher-lg">
      <CustomTypography variant={"subtitle1"}>{cardholder}</CustomTypography>
      <RadioButtonContainer>
        <RadioButton
          disabled={isUploadInProgress}
          selectedValue={activeAttachmentDocumentType}
          id="person-document-upload-radiobutton"
          name="person-document-upload-radiobutton"
          values={radioButtonItems}
          onClick={(radioButtonItem: RadioButtonItem) => {
            setActiveAttachmentDocumentType(
              radioButtonItem.value as CardApplicationAttachmentsDocument["attachmentDocumentType"],
            );
          }}
        />
      </RadioButtonContainer>
      <GridItem>
        <div className="scd-upload-wrapper">
          {activeAttachmentDocumentType && (
            <Uploader
              preloadedUpload={getAttachmentMeta({
                attachmentDocumentType: activeAttachmentDocumentType,
                getFirstAttachment: true,
              })}
              dataForUploading={{
                applicationRef,
                attachmentType: activeAttachmentDocumentType,
              }}
              uploadInProgress={{
                isUploadInProgress,
                setIsUploadInProgress,
              }}
            />
          )}
          {isOneMoreUploadForEdgeCase && (
            <Uploader
              preloadedUpload={getAttachmentMeta({
                attachmentDocumentType: activeAttachmentDocumentType,
                getSecondAttachment: true,
              })}
              dataForUploading={{
                applicationRef,
                attachmentType: activeAttachmentDocumentType,
              }}
              uploadInProgress={{
                isUploadInProgress,
                setIsUploadInProgress,
              }}
            />
          )}
        </div>
      </GridItem>
    </div>
  );
};
